import { useState, useMemo, useRef, useEffect } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../../lib/utils";
import useOrganizationProjects from "../../pages/projects/hooks/useOrganizationProjects";
import { Project } from "../../__generated__/graphql";

// Remove accents for better search matching
const removeAccents = (str: string) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

interface ProjectsSelectionProps {
  selectedProjectId?: string | null;
  onProjectSelect: (project: Project | null) => void;
  placeholder?: string;
  className?: string;
  label?: string;
  error?: string;
}

export default function ProjectsSelection({
  selectedProjectId,
  onProjectSelect,
  placeholder = "Sélectionner un projet...",
  className = "",
  label,
  error,
}: ProjectsSelectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data, loading, error: fetchError } = useOrganizationProjects();

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    const projects = data?.projects || [];
    if (!searchQuery.trim()) return projects;

    return projects.filter((project) =>
      removeAccents(project.name.toLowerCase()).includes(
        removeAccents(searchQuery.toLowerCase())
      )
    );
  }, [data?.projects, searchQuery]);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift()],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset search and highlighted index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered projects change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredProjects]);

  const handleProjectSelect = (project: Project) => {
    onProjectSelect(project);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    onProjectSelect(null);
    setIsOpen(false);
  };

  // Enhanced keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case "Escape":
        setIsOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredProjects.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        event.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredProjects.length
        ) {
          handleProjectSelect(filteredProjects[highlightedIndex]);
        } else if (highlightedIndex === -1 && selectedProjectId) {
          // If no item is highlighted but there's a selection, clear it
          handleClearSelection();
        }
        break;
    }
  };

  // Handle keyboard navigation for search input
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    handleKeyDown(event);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-1">{label}</label>
      )}

      <div className="relative">
        <button
          type="button"
          ref={refs.setReference}
          className={cn(
            "w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border text-left flex items-center justify-between cursor-pointer hover:bg-input/80",
            error && "border-red-500"
          )}
          {...getReferenceProps()}
          onKeyDown={handleKeyDown}
        >
          <span
            className={cn("truncate", !selectedProjectId && "text-tertiary")}
          >
            {selectedProjectId
              ? data?.projects?.find(
                  (project) => project?.id === selectedProjectId
                )?.name
              : placeholder}
          </span>
          <ChevronDownIcon
            className={cn(
              "w-4 h-4 text-secondary transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>

        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 w-full min-w-[200px] max-w-[400px] rounded-md bg-input border border-border shadow-lg"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Rechercher un projet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 bg-background rounded border border-border focus:outline-none focus:ring-1 focus:ring-accent text-sm placeholder:text-tertiary"
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            </div>

            {/* Projects List */}
            <div className="max-h-60 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="p-3 text-sm text-tertiary text-center">
                  Chargement des projets...
                </div>
              ) : fetchError ? (
                <div className="p-3 text-sm text-red-500 text-center">
                  Erreur lors du chargement des projets
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="p-3 text-sm text-tertiary text-center">
                  {searchQuery
                    ? "Aucun projet trouvé"
                    : "Aucun projet disponible"}
                </div>
              ) : (
                <>
                  {/* Clear selection option */}
                  {selectedProjectId && (
                    <button
                      type="button"
                      onClick={handleClearSelection}
                      className="w-full px-3 py-2 text-left hover:bg-card text-sm text-secondary border-b border-border flex items-center justify-between cursor-pointer"
                    >
                      Supprimer la sélection
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}

                  {/* Project options */}
                  {filteredProjects.map((project, index) => (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => handleProjectSelect(project)}
                      className={cn(
                        "w-full px-3 py-2 text-left hover:bg-card text-sm transition-colors duration-150",
                        selectedProjectId === project?.id &&
                          "bg-card text-accent",
                        highlightedIndex === index && "bg-selected"
                      )}
                    >
                      <div className="font-medium">{project.name}</div>
                      {project.description && (
                        <div className="text-xs text-tertiary mt-1 truncate">
                          {project.description}
                        </div>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
